/**
 * Install global axios + fetch interceptors that send `X-Session-Id` on
 * every backend request and capture the server's echoed sid off every
 * response (issue #1447).
 *
 * The interceptors are passive — they attach a header on the way out
 * and persist the server's sid on the way in. They do NOT swallow
 * errors, retry, or transform responses.
 */

import axios from "axios";

import { getSessionId, recordSessionIdFromResponse } from "./sessionIdTracker";

const HEADER_LOWER = "x-session-id";
const HEADER_CANONICAL = "X-Session-Id";

let installed = false;

export function installSessionIdInterceptor(): void {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const sid = getSessionId();
    const headers = config.headers as unknown;
    if (headers && typeof (headers as { set?: unknown }).set === "function") {
      // AxiosHeaders instance: use the typed setter so internal accessors
      // (case-insensitive lookup, normalisation) work as expected.
      (headers as { set: (k: string, v: string) => void }).set(HEADER_CANONICAL, sid);
    } else if (headers && typeof headers === "object") {
      (headers as Record<string, string>)[HEADER_CANONICAL] = sid;
    } else {
      (config as { headers: Record<string, string> }).headers = { [HEADER_CANONICAL]: sid };
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const sid = response?.headers?.[HEADER_LOWER];
      if (typeof sid === "string" && sid.length > 0) {
        recordSessionIdFromResponse(sid);
      }
      return response;
    },
    (error) => {
      const sid = error?.response?.headers?.[HEADER_LOWER];
      if (typeof sid === "string" && sid.length > 0) {
        recordSessionIdFromResponse(sid);
      }
      return Promise.reject(error);
    },
  );

  // Also hook raw `fetch`. Apollo's HttpLink, Playwright tests, and
  // analytics flushes go through fetch rather than axios.
  if (typeof window !== "undefined" && typeof window.fetch === "function") {
    const original = window.fetch.bind(window);
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const sid = getSessionId();
      const init = (args[1] as RequestInit | undefined) ?? {};
      const headers = new Headers(init.headers ?? {});
      if (!headers.has(HEADER_CANONICAL)) {
        headers.set(HEADER_CANONICAL, sid);
      }
      const newInit: RequestInit = { ...init, headers };
      const response = await original(args[0], newInit);
      try {
        const responseSid = response.headers.get(HEADER_LOWER);
        if (responseSid) recordSessionIdFromResponse(responseSid);
      } catch {
        /* never break the response on a tracking failure */
      }
      return response;
    };
  }
}
